import { useRef, useState } from 'react';
import { usePharmacyStores } from '../usePharmacyStores';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, NativeSelect } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { LoadingState, EmptyState } from '@/components/ui/States';
import { ApiError } from '@/lib/api/client';
import { useBillingSettings } from '@/hooks/useBillingSettings';
import { useVendors } from '../useVendors';
import { useGoodsReceipts } from '../useGoodsReceipts';
import { useCreateGoodsReceipt } from '../useCreateGoodsReceipt';
import GrnLineEditor, { emptyGrnLine, toGrnItemInput, type DraftGrnLine } from './GrnLineEditor';
import ProcurementInvoiceQRModal from './ProcurementInvoiceQRModal';
import { linesFromExtractedInvoice } from './extractedInvoiceLines';
import { useInvoiceUploadFlow } from './useInvoiceUploadFlow';
import { useAiAvailability } from '@/features/ai-assist/useAiAvailability';

export default function GoodsReceiptPanel() {
  // AI off (§4.4): "Scan invoice via phone" OCRs/extracts via AI server-side
  // (utils/vendorInvoiceExtraction.js) — hidden, manual line entry only.
  const { aiEnabled } = useAiAvailability();
  const { gstPercentageOptions } = useBillingSettings();
  const [creating, setCreating] = useState(false);
  const [vendorId, setVendorId] = useState('');
  const [invoiceNo, setInvoiceNo] = useState('');
  const [invoiceDiscountPct, setInvoiceDiscountPct] = useState('');
  const [items, setItems] = useState<DraftGrnLine[]>([emptyGrnLine(gstPercentageOptions)]);
  const [validationError, setValidationError] = useState('');
  const [showQr, setShowQr] = useState(false);
  // undefined = the branch's receiving store, decided by the server.
  const [storeId, setStoreId] = useState<number | undefined>(undefined);
  const storesQuery = usePharmacyStores();
  const receivingStores = (storesQuery.data?.stores ?? []).filter((s) => s.active && s.can_receive);
  const defaultReceiving = storesQuery.data?.stores.find((s) => s.id === storesQuery.data?.channels.receiving);
  const fileRef = useRef<HTMLInputElement>(null);

  const vendorsQuery = useVendors();
  const grnQuery = useGoodsReceipts();
  const createGrn = useCreateGoodsReceipt();
  const invoiceFlow = useInvoiceUploadFlow((lines) => setItems(linesFromExtractedInvoice(lines, gstPercentageOptions)));

  const handleFilePick = (file: File | undefined) => {
    if (!file) return;
    setCreating(true);
    invoiceFlow.handleFilePick(file);
    if (fileRef.current) fileRef.current.value = '';
  };

  // Mirrors POST /goods-receipts' own baseAmount/discount precedence exactly
  // (backend/routes/pharmacy-procurement.js) — this preview must never
  // silently disagree with what actually gets saved. Base amount, per line:
  // the invoice's own printed Amount if given, else paid strips × rate
  // (never inflated by pack size or free stock), else quantity × rate for a
  // flat-quantity line with no strip breakdown at all.
  const baseAmount = (it: DraftGrnLine) => {
    const rate = parseFloat(it.unit_cost) || 0;
    if (it.line_amount) return parseFloat(it.line_amount) || 0;
    const strips = parseInt(it.strips_received, 10);
    if (!Number.isNaN(strips)) return strips * rate;
    return rate * (parseInt(it.quantity_received, 10) || 0);
  };
  // gross -> item discount -> invoice discount -> net -> GST, same two-stage
  // order the backend applies. Labeled the same way the source invoice
  // itself labels these (Sub Total / Discount / Taxable Value / GST / Net
  // Amount) so the preview is directly checkable line-for-line against a
  // printed invoice, not just internally consistent.
  const itemNet = (it: DraftGrnLine) => {
    const itemDisc = parseFloat(it.discount_percent) || 0;
    return baseAmount(it) * (1 - itemDisc / 100);
  };
  const subtotal = items.reduce((s, it) => s + itemNet(it), 0);
  const invoiceDiscountAmt = subtotal * (Math.min(100, Math.max(0, parseFloat(invoiceDiscountPct) || 0)) / 100);
  const invoiceDiscFrac = subtotal > 0 ? 1 - invoiceDiscountAmt / subtotal : 1;
  const taxableValue = subtotal - invoiceDiscountAmt;
  const gstAmt = items.reduce((s, it) => s + itemNet(it) * invoiceDiscFrac * ((parseFloat(it.gst_percent) || 0) / 100), 0);
  const total = taxableValue + gstAmt;
  // Standard Indian-invoice "Round Off" — the exact taxable+GST figure
  // carries paise, but the invoice's own printed total is rounded to the
  // nearest whole rupee (e.g. ₹82,810.90 prints as ₹82,811).
  const roundedTotal = Math.round(total);
  const roundOff = roundedTotal - total;

  const submit = () => {
    const clean = items.filter((i) => i.medicine_id && i.batch_number.trim() && i.expiry_date && i.quantity_received && i.mrp);
    if (!vendorId || !clean.length) {
      setValidationError('Vendor and at least one complete item (medicine selected, batch, expiry, qty, MRP) are required');
      return;
    }
    setValidationError('');
    createGrn.mutate(
      {
        vendor_id: Number(vendorId),
        vendor_invoice_number: invoiceNo.trim() || null,
        invoice_discount_percent: invoiceDiscountPct ? parseFloat(invoiceDiscountPct) : undefined,
        store_id: storeId,
        items: clean.map(toGrnItemInput),
      },
      {
        onSuccess: () => {
          setVendorId('');
          setInvoiceNo('');
          setInvoiceDiscountPct('');
          setItems([emptyGrnLine(gstPercentageOptions)]);
          invoiceFlow.clearNotice();
          setCreating(false);
        },
      }
    );
  };

  const error = validationError || (createGrn.error instanceof ApiError ? createGrn.error.message : null);
  const vendors = vendorsQuery.data;
  const grns = grnQuery.data;

  if (vendorsQuery.isLoading || grnQuery.isLoading || vendors === undefined || grns === undefined) return <LoadingState />;

  return (
    <Card>
      <div className="mb-3.5 flex flex-wrap items-center justify-between gap-1.5">
        <div className="text-sm font-semibold">Goods Receipt Notes</div>
        <div className="flex flex-wrap gap-1.5">
          {aiEnabled && (
            <>
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/tiff,application/pdf" className="hidden" onChange={(e) => handleFilePick(e.target.files?.[0])} />
              <Button variant="ghost" size="sm" disabled={!vendors.length || invoiceFlow.isBusy} loading={invoiceFlow.isBusy} onClick={() => fileRef.current?.click()}>
                📄 Upload vendor invoice
              </Button>
              <Button variant="ghost" size="sm" disabled={!vendors.length} onClick={() => { setCreating(true); setShowQr(true); }}>📱 Scan invoice via phone</Button>
            </>
          )}
          {!creating && <Button variant="ghost" size="sm" disabled={!vendors.length} onClick={() => setCreating(true)}>+ Receive stock</Button>}
        </div>
      </div>
      {!vendors.length && <div className="mb-2.5 text-xs text-[#9CA3AF]">Add a vendor first, under the Vendors tab.</div>}
      {error && <div className="mb-3 rounded-[var(--radius)] bg-danger-soft px-3 py-2 text-xs text-danger-ink">{error}</div>}
      {creating && (
        <div className="mb-3.5 rounded-[var(--radius)] bg-canvas p-3">
          {invoiceFlow.uploadNotice && (
            <div className="mb-2.5 flex items-center gap-2 rounded-[var(--radius)] bg-info-soft px-3 py-2 text-[11.5px] text-info-ink">
              {invoiceFlow.isBusy && (
                <span aria-hidden="true" className="h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent" />
              )}
              {invoiceFlow.uploadNotice}
            </div>
          )}

          <div className="mb-2.5 grid grid-cols-3 gap-2.5">
            <FormField label="Vendor">
              <NativeSelect value={vendorId} onChange={(e) => setVendorId(e.target.value)}>
                <option value="">Select vendor…</option>
                {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
              </NativeSelect>
            </FormField>
            <FormField label="Vendor invoice no."><Input value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)} /></FormField>
            <FormField label="Invoice discount %">
              <Input
                type="number" value={invoiceDiscountPct} onChange={(e) => setInvoiceDiscountPct(e.target.value)} placeholder="0"
                title="One discount applied to the whole invoice's subtotal before GST, if the vendor's invoice shows one — separate from any per-line discount below."
              />
            </FormField>
          </div>

          <GrnLineEditor items={items} onChange={setItems} gstPercentageOptions={gstPercentageOptions} />

          <div className="mb-3 mt-2.5 rounded-[var(--radius)] bg-canvas p-3">
            {(() => {
              const rows: [string, number][] = [
                ['Subtotal', subtotal],
                ...(invoiceDiscountAmt > 0 ? ([['Invoice discount', -invoiceDiscountAmt], ['Taxable value', taxableValue]] as [string, number][]) : []),
                ['GST', gstAmt],
                ...(roundOff !== 0 ? ([['Round off', roundOff]] as [string, number][]) : []),
                ['Total', roundedTotal],
              ];
              return rows.map(([l, v], i, arr) => (
                <div
                  key={l}
                  className={`flex justify-between ${i < arr.length - 1 ? 'mb-1.5' : ''} ${i === arr.length - 1 ? 'border-t border-line pt-2 text-base font-bold' : 'text-[13px]'}`}
                >
                  {l}
                  <span>₹{v.toFixed(2)}</span>
                </div>
              ));
            })()}
          </div>

          {receivingStores.length > 1 && (
            <label className="mb-3 flex flex-wrap items-center gap-2 text-[13px]">
              <span className="font-semibold">Receive into</span>
              <select
                className="h-9 w-auto min-w-[200px] rounded-[var(--radius)] border border-input bg-card px-2 text-[13px]"
                value={storeId ?? ''}
                onChange={(e) => setStoreId(e.target.value ? Number(e.target.value) : undefined)}
              >
                <option value="">{defaultReceiving ? `${defaultReceiving.name} (default)` : 'Default receiving store'}</option>
                {receivingStores.filter((s) => s.id !== defaultReceiving?.id).map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </label>
          )}
          <div className="flex gap-2">
            <Button size="sm" disabled={createGrn.isPending} loading={createGrn.isPending} onClick={submit}>
              {createGrn.isPending ? 'Saving…' : 'Save GRN & add to stock'}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => { setCreating(false); invoiceFlow.clearNotice(); }}>Cancel</Button>
          </div>
        </div>
      )}
      <div className="flex flex-col gap-2">
        {grns.map((g) => (
          <div key={g.id} className="rounded-[var(--radius)] border border-line p-2.5">
            <div className="text-[13px] font-semibold">{g.grn_number} — {g.vendor_name}{g.po_number ? ` (${g.po_number})` : ''}</div>
            <div className="mt-1 text-[11.5px] text-ink-muted">
              {new Date(g.received_at).toLocaleString('en-IN')} · {g.items.map((i) => `${i.generic_name} ${i.strength || ''} ×${i.quantity_received}`).join(', ')}
            </div>
          </div>
        ))}
        {grns.length === 0 && <EmptyState label="No goods receipts yet." />}
      </div>

      {showQr && (
        <ProcurementInvoiceQRModal
          onClose={() => setShowQr(false)}
          onReceived={(lines) => { invoiceFlow.handleReceived(lines); setShowQr(false); }}
        />
      )}
    </Card>
  );
}
